import { Box, CardActionArea, Drawer, Link, Stack, Tooltip, Typography } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { MHidden } from 'components/@material-extend';
import Logo from 'components/Logo';
import MyAvatar from 'components/MyAvatar';
import SidebarNavRegion from './SidebarNavRegion';
import useAuth from 'hooks/useAuth';
import useCollapseDrawer from 'hooks/useCollapseDrawer';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useParams } from 'react-router';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { PATH_APP } from 'routes/paths';
import sidebarConfig, { APPLICATIONS_MAP } from './SidebarConfig';

const DRAWER_WIDTH = 280;
const COLLAPSE_WIDTH = 102;

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    transition: theme.transitions.create('width', {
      duration: theme.transitions.duration.complex,
    }),
  },
}));

const AccountStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2, 2.5),
  borderRadius: theme.shape.borderRadiusSm,
  backgroundColor: theme.palette.grey[500_12],
}));

IconCollapse.propTypes = {
  onToggleCollapse: PropTypes.func,
  collapseClick: PropTypes.bool,
};

function IconCollapse({ onToggleCollapse, collapseClick }) {
  return (
    <Tooltip title='Mini Menu'>
      <CardActionArea
        onClick={onToggleCollapse}
        sx={{
          width: 18,
          height: 18,
          display: 'flex',
          cursor: 'pointer',
          borderRadius: '50%',
          alignItems: 'center',
          color: 'text.primary',
          justifyContent: 'center',
          border: 'solid 1px currentColor',
          ...(collapseClick && {
            borderWidth: 2,
          }),
        }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: 'currentColor',
            transition: (theme) => theme.transitions.create('all'),
            ...(collapseClick && {
              width: 0,
              height: 0,
            }),
          }}
        />
      </CardActionArea>
    </Tooltip>
  );
}

AppSidebar.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
};

export default function AppSidebar({ isOpenSidebar, onCloseSidebar, currentPage }) {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const { appId } = useParams();
  const { isCollapse, collapseClick, collapseHover, onToggleCollapse, onHoverEnter, onHoverLeave } =
    useCollapseDrawer();

  const navConfig = sidebarConfig({ appId, organizationRole: user?.organizationRole })[currentPage];
  const pinSettingsFooter = currentPage === 'applications' && Array.isArray(navConfig) && navConfig.length >= 2;

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const getSidebarHeader = () => {
    switch (currentPage) {
      case 'adminOverview':
        return isCollapse ? (
          <MyAvatar sx={{ mx: 'auto', mb: 2 }} />
        ) : (
          <Link underline='none' component={RouterLink} to={PATH_APP.admin.account}>
            <AccountStyle>
              <MyAvatar />
              <Box sx={{ ml: 2 }}>
                <Typography variant='subtitle2' sx={{ color: 'text.primary' }}>
                  {user?.displayName}
                </Typography>
                <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                  {user?.role}
                </Typography>
              </Box>
            </AccountStyle>
          </Link>
        );
      case 'applications':
        return (
          <>
            {isCollapse ? (
              <MyAvatar sx={{ mx: 'auto', mb: 2 }} />
            ) : (
              <Link underline='none' component={RouterLink} to={PATH_APP.admin.account}>
                <AccountStyle>
                  <MyAvatar />
                  <Box sx={{ ml: 2 }}>
                    <Typography variant='subtitle2' sx={{ color: 'text.primary' }}>
                      {user?.displayName}
                    </Typography>
                    <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                      {APPLICATIONS_MAP[appId] || ''}
                    </Typography>
                  </Box>
                </AccountStyle>
              </Link>
            )}
          </>
        );
      default:
        break;
    }
  };

  const renderContent = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Stack
        spacing={3}
        sx={{
          flexShrink: 0,
          px: 2.5,
          pt: 3,
          pb: 2,
          ...(isCollapse && {
            alignItems: 'center',
          }),
        }}
      >
        <Stack direction='row' alignItems='center' justifyContent='space-between'>
          <Box component={RouterLink} to='/admin/overview' sx={{ display: 'inline-flex' }}>
            <Logo sx={{ mb: 2 }} />
          </Box>

          <MHidden width='lgDown'>
            {!isCollapse && <IconCollapse onToggleCollapse={onToggleCollapse} collapseClick={collapseClick} />}
          </MHidden>
        </Stack>

        {getSidebarHeader()}
      </Stack>

      {pinSettingsFooter ? (
        <SidebarNavRegion
          mainNavConfig={navConfig.slice(0, -1)}
          footerNavConfig={navConfig.slice(-1)}
          isShow={!isCollapse}
        />
      ) : (
        <SidebarNavRegion mainNavConfig={navConfig} isShow={!isCollapse} mainNavSx={{ pb: 2 }} />
      )}
    </Box>
  );

  return (
    <RootStyle
      sx={{
        width: {
          lg: isCollapse ? COLLAPSE_WIDTH : DRAWER_WIDTH,
        },
        ...(collapseClick && {
          position: 'absolute',
        }),
      }}
    >
      <MHidden width='lgUp'>
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{
            sx: { width: DRAWER_WIDTH, height: '100vh', display: 'flex', flexDirection: 'column' },
          }}
        >
          {renderContent}
        </Drawer>
      </MHidden>

      <MHidden width='lgDown'>
        <Drawer
          open
          variant='persistent'
          onMouseEnter={onHoverEnter}
          onMouseLeave={onHoverLeave}
          PaperProps={{
            sx: {
              width: DRAWER_WIDTH,
              height: '100vh',
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'background.default',
              ...(isCollapse && {
                width: COLLAPSE_WIDTH,
              }),
              ...(collapseHover && {
                borderRight: 0,
                backdropFilter: 'blur(6px)',
                WebkitBackdropFilter: 'blur(6px)', // Fix on Mobile
                boxShadow: (theme) => theme.customShadows.z20,
                bgcolor: (theme) => alpha(theme.palette.background.default, 0.88),
              }),
            },
          }}
        >
          {renderContent}
        </Drawer>
      </MHidden>
    </RootStyle>
  );
}
